import MinimalLoginForm from "../components/MinimalLoginForm"
import { Navigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useRef, useEffect } from "react"

const NeuralBackground = ({ isDark }) => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -100, y: -100 });
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize nodes
    const nodes = [];
    const isMobile = window.innerWidth <= 768;
    for (let i = 0; i < (isMobile ? 30 : 120); i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: Math.random() * 2 - 1,
        vy: Math.random() * 2 - 1,
        size: Math.random() * 2 + 2,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01,
        energy: Math.random() * 0.5 + 0.5
      });
    }
    nodesRef.current = nodes;

    const animate = () => {
      if (!ctx || !canvas) return;

      // Create professional gradient background
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      if (isDark) {
        gradient.addColorStop(0, '#0d1117');
        gradient.addColorStop(0.3, '#161b22');
        gradient.addColorStop(0.7, '#21262d');
        gradient.addColorStop(1, '#0d1117');
      } else {
        gradient.addColorStop(0, '#f6f8fa');
        gradient.addColorStop(0.3, '#ffffff');
        gradient.addColorStop(0.7, '#f6f8fa');
        gradient.addColorStop(1, '#f6f8fa');
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add subtle grid pattern
      ctx.strokeStyle = isDark ? 'rgba(88, 166, 255, 0.08)' : 'rgba(9, 105, 218, 0.08)';
      ctx.lineWidth = 1;
      const gridSize = 60;
      
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Update and draw nodes
      for (let node of nodes) {
        let dx = mouseRef.current.x - node.x;
        let dy = mouseRef.current.y - node.y;
        let distance = Math.sqrt(dx * dx + dy * dy);

        // Enhanced mouse interaction
        if (distance < 150) {
          const force = (150 - distance) / 150;
          node.vx += (dx / distance) * force * 0.02;
          node.vy += (dy / distance) * force * 0.02;
          node.energy = Math.min(1, node.energy + force * 0.1);
        } else {
          node.energy *= 0.995;
        }

        node.x += node.vx;
        node.y += node.vy;

        // Add some randomness
        node.vx += (Math.random() - 0.5) * 0.1;
        node.vy += (Math.random() - 0.5) * 0.1;

        // Apply friction
        node.vx *= 0.98;
        node.vy *= 0.98;

        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) {
          node.vx *= -0.8;
          node.x = Math.max(0, Math.min(canvas.width, node.x));
        }
        if (node.y < 0 || node.y > canvas.height) {
          node.vy *= -0.8;
          node.y = Math.max(0, Math.min(canvas.height, node.y));
        }

        // Update pulse
        node.pulse += node.pulseSpeed;
        const pulseSize = node.size + Math.sin(node.pulse) * 0.8;

        // Draw node with professional styling
        const nodeSize = pulseSize + node.energy * 1.5;
        
        // Main node with subtle gradient
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, nodeSize
        );
        gradient.addColorStop(0, isDark ? '#58a6ff' : '#0969da');
        gradient.addColorStop(0.7, isDark ? '#1f6feb' : '#0550ae');
        gradient.addColorStop(1, isDark ? '#0969da' : '#033d8b');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Subtle border
        ctx.strokeStyle = isDark ? 'rgba(88, 166, 255, 0.3)' : 'rgba(9, 105, 218, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize, 0, Math.PI * 2);
        ctx.stroke();
        
        // Inner core
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw connections with enhanced effects
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          let dx = nodes[i].x - nodes[j].x;
          let dy = nodes[i].y - nodes[j].y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 120) {
            const opacity = (1 - distance / 120) * 0.4;
            const lineWidth = (1 - distance / 120) * 1.5 + 0.5;
            
            // Enhanced connection based on node energy
            const energyBoost = (nodes[i].energy + nodes[j].energy) * 0.5;
            const enhancedOpacity = Math.min(0.8, opacity + energyBoost * 0.3);
            
            // Create professional gradient for connection
            const lineGradient = ctx.createLinearGradient(
              nodes[i].x, nodes[i].y,
              nodes[j].x, nodes[j].y
            );
            
            if (isDark) {
              lineGradient.addColorStop(0, `rgba(88, 166, 255, ${enhancedOpacity})`);
              lineGradient.addColorStop(0.5, `rgba(31, 111, 235, ${enhancedOpacity * 0.8})`);
              lineGradient.addColorStop(1, `rgba(9, 105, 218, ${enhancedOpacity})`);
            } else {
              lineGradient.addColorStop(0, `rgba(9, 105, 218, ${enhancedOpacity})`);
              lineGradient.addColorStop(0.5, `rgba(5, 80, 174, ${enhancedOpacity * 0.8})`);
              lineGradient.addColorStop(1, `rgba(3, 61, 139, ${enhancedOpacity})`);
            }

            ctx.strokeStyle = lineGradient;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      requestAnimationFrame(animate);
    };

    animate();

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -1,
        pointerEvents: 'none'
      }}
    />
  );
};

const LoginPage = () => {
    const user = useSelector(state => state.store.user);
    const isAuth = user.isAuth;
    const isDarkMode = useSelector(state => state.settings.darkMode);
    
    if (isAuth) {
        return <Navigate to='/map' />
    }
    
    return (
        <div className="login__container" style={{ width: '100%', height: '100%' }}>
            <NeuralBackground isDark={isDarkMode} />
            <MinimalLoginForm />
        </div>
    )
}

export default LoginPage